import { type FC, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { Button } from './ui/button';
import { Check, Loader2 } from 'lucide-react';
import { JoinTreeLabel } from './JoinTreeLabel';
import { CustomPathBuilder } from './CustomPathBuilder';
import { usePipelineNodes } from '../hooks/usePipelineNodes';
import type {
  MeasureJoinTreeNode,
  DimensionJoinTree,
  JoinPathOption,
  DataWhitelist,
} from '../services/measure.service';
import { cn } from '@/lib/utils';

interface JoinPathSelectionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activePipelineNodeId: string | null;
  dimensionJoinTree: DimensionJoinTree | undefined;
  joinPathOptions: JoinPathOption[];
  metricPipelineNodeId: string;
  whitelistNodeIds: string[];
  dataWhitelist: DataWhitelist;
  onSelectPath: (pipelineNodeId: string, pathIndex: number) => void;
  onCustomPathSave: (pipelineNodeId: string, customPath: MeasureJoinTreeNode) => void;
  isLoading?: boolean;
}

// Helper to extract all relationship IDs from a join tree
function getRelationshipIds(tree: MeasureJoinTreeNode): string[] {
  const ids: string[] = [];
  if (tree.relationship_id) {
    ids.push(tree.relationship_id);
  }
  for (const downstream of tree.downstream || []) {
    ids.push(...getRelationshipIds(downstream));
  }
  return ids;
}

// Helper to compare paths by their relationship IDs
function arePathsEqual(
  path1: MeasureJoinTreeNode | null,
  path2: MeasureJoinTreeNode
): boolean {
  if (!path1) return false;
  const ids1 = getRelationshipIds(path1).sort();
  const ids2 = getRelationshipIds(path2).sort();
  return JSON.stringify(ids1) === JSON.stringify(ids2);
}

// Helper to calculate path length (number of nodes)
function getPathLength(node: MeasureJoinTreeNode): number {
  let length = 1;
  if (node.downstream) {
    node.downstream.forEach((d) => {
      length += getPathLength(d);
    });
  }
  return length;
}

export const JoinPathSelectionSheet: FC<JoinPathSelectionSheetProps> = ({
  open,
  onOpenChange,
  activePipelineNodeId,
  dimensionJoinTree,
  joinPathOptions,
  metricPipelineNodeId,
  whitelistNodeIds,
  dataWhitelist,
  onSelectPath,
  onCustomPathSave,
  isLoading,
}) => {
  const { data: nodesData } = usePipelineNodes({ page: 1, pageSize: 1000 });

  const nodesById = useMemo(() => {
    if (!nodesData?.records) return new Map();
    return new Map(nodesData.records.map((n) => [n.id, n]));
  }, [nodesData]);

  const activePathOption = useMemo(() => {
    if (!activePipelineNodeId) return null;
    return joinPathOptions.find(
      (opt) => opt.pipeline_node_id === activePipelineNodeId
    );
  }, [activePipelineNodeId, joinPathOptions]);

  // Sort paths by length (shortest first)
  const sortedPaths = useMemo(() => {
    if (!activePathOption?.possible_paths) return [];
    return [...activePathOption.possible_paths].sort((a, b) => {
      return getPathLength(a) - getPathLength(b);
    });
  }, [activePathOption]);

  const highlightNodeIds = useMemo(() => {
    if (!activePipelineNodeId) return [];
    return [activePipelineNodeId, metricPipelineNodeId, ...whitelistNodeIds];
  }, [activePipelineNodeId, metricPipelineNodeId, whitelistNodeIds]);

  const nodeName = activePipelineNodeId
    ? nodesById.get(activePipelineNodeId)?.label ||
      nodesById.get(activePipelineNodeId)?.name ||
      activePipelineNodeId
    : '';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Select Join Path</SheetTitle>
          <SheetDescription>
            Choose how this metric connects to <strong>{nodeName}</strong> and
            any filters you have set up.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : sortedPaths.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">
              No paths available for this dimension.
            </p>
          ) : (
            sortedPaths.map((path, idx) => {
              const isSelected = dimensionJoinTree?.join_tree
                ? arePathsEqual(dimensionJoinTree.join_tree, path)
                : false;

              return (
                <div
                  key={idx}
                  className={cn(
                    'border rounded-lg p-3 transition-colors',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'hover:border-muted-foreground/50'
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <JoinTreeLabel
                        joinTree={path}
                        highlightNodeIds={highlightNodeIds}
                      />
                    </div>
                    <Button
                      variant={isSelected ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        if (activePipelineNodeId) {
                          onSelectPath(activePipelineNodeId, idx);
                        }
                      }}
                      disabled={isSelected}
                      className="flex-shrink-0"
                    >
                      {isSelected ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Selected
                        </>
                      ) : (
                        'Select'
                      )}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Custom Path Builder */}
        {!isLoading && activePipelineNodeId && (
          <div className="mt-6">
            <CustomPathBuilder
              startNodeId={metricPipelineNodeId}
              targetDimensionNodeId={activePipelineNodeId}
              dataWhitelist={dataWhitelist}
              existingCustomPath={dimensionJoinTree?.custom_path || undefined}
              onSave={(customPath) => {
                onCustomPathSave(activePipelineNodeId, customPath);
              }}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
